import {
  Box,
  Button,
  Image,
  Flex,
  Text,
  Heading,
  Icon,
  Container,
  Stack,
  HStack,
  List,
  ListItem,
} from "@chakra-ui/react";
import ComplianceBanner from "components/compliance-banner";
import Head from "next/head";
import { FiCheck } from "react-icons/fi";
import { ImQuotesLeft, ImQuotesRight } from "react-icons/im";
import BookBusinessDemo from "../../components/calendly/book-business-demo";
import { Section } from "../../components/section";
import { StdLink } from "../../components/StdLink";
import TallyForm from "../../components/tally-form";

export default function Schools() {
  return (
    <>
      <Head>
        <title>Talkio for Teams</title>
        <meta property="og:title" content={"Talkio for Teams"} key="title" />
      </Head>

      <Box>
        <Section>
          <Flex flexDir="column" flex="1">
            <Heading
              as="h1"
              color={"brand.gray"}
              fontSize={"6xl"}
              fontWeight="bold"
              lineHeight={"4rem"}
              letterSpacing={".001rem"}
              mt={{ base: "30px", lg: "80px", xl: "70px" }}
            >
              Talkio for Teams
            </Heading>
            <Text mt={4}>
              Enhance your
              {` team's `}
              <Box as="span" color="brand.primary.600">
                language proficiency
              </Box>
              {" and "}
              <Box as="span" color="brand.primary.600">
                workplace communication
              </Box>{" "}
              with Talkio for Teams
            </Text>

            <List my={8}>
              {[
                "Access to all Talkio functionalities",
                "Multi-user management",
                "Employee progress tracking",
                "Discounted per-user rates",
                "GDPR Compliant & Enterprise Security",
              ].map((feature, i) => (
                <ListItem key={i}>
                  <HStack spacing="4">
                    <Icon as={FiCheck} color="brand.green.400" boxSize="6" />
                    <Text fontSize="sm" textAlign="left">
                      {feature}
                    </Text>
                  </HStack>
                </ListItem>
              ))}
            </List>
            <Button
              colorScheme={"brand.primary"}
              color={"brand.gray"}
              maxW={{ base: "100%", sm: "300px" }}
              as="a"
              href="#pricing"
              mb={12}
            >
              Book Demo
            </Button>
          </Flex>
          <Flex display={{ base: "none", md: "flex" }} flex="1" justifyContent={{ base: "center" }}>
            <Image
              src="/images/talkio-business-1.webp"
              alt="Landing"
              alignSelf={"center"}
              mx={2}
              width={{ base: "250px", sm: "350px", md: "450px" }}
            />
          </Flex>
        </Section>
      </Box>

      <Box backgroundColor="gray.700">
        <Section>
          <Flex my={12} flexDir={"column"} flex="1" color="#fff">
            <Text mx="auto" maxW="600px" textAlign={"center"} fontSize="md">
              Direct interaction and communication in a native or second language is crucial for
              efficient teamwork. Regular, personalized language practice for each employee would
              greatly enhance proficiency but can be challenging to implement widely.
            </Text>
            <Text textAlign={"center"} fontSize="xl" mt={7} fontWeight="bold">
              Imagine a more efficient way to develop these essential speaking skills
            </Text>
          </Flex>
        </Section>
      </Box>

      <Section>
        <Flex flexDir={"column"} flex="1" justifyContent={"center"}>
          <Heading
            color={"brand.gray"}
            fontSize={{ base: "3xl", md: "4xl" }}
            fontWeight="bold"
            lineHeight={1}
            my={5}
          >
            Introducing Talkio for Teams
          </Heading>
          <Text mt="4">
            Talkio for Teams enables employees to refine their language skills through{" "}
            <strong>interactive AI conversations</strong>, mimicking real-life exchanges. They
            receive specific feedback on pronunciation, grammar, and errors, allowing for a
            customized learning journey. This approach allows fo{" "}
            <strong>independent language practice</strong>, empowering employees to enhance their
            communication skills in their own time.
          </Text>
          <Text mt={3}>
            This is beneficial for both the company and the employees, enhancing workplace
            efficiency and individual skill sets.
          </Text>
        </Flex>
        <Flex flex="1" justifyContent={"center"}>
          <Image
            src="/images/talkio-business-2.webp"
            alt="Woman speaking with AI"
            height={"auto"}
            alignSelf={"center"}
            width={{ base: "250px", sm: "350px", md: "450px", lg: "100%" }}
          />
        </Flex>
      </Section>

      <Flex
        backgroundColor="gray.700"
        color="#fff"
        textAlign={"center"}
        p={12}
        justifyContent={"center"}
      >
        <Icon as={ImQuotesLeft} fontSize="2xl" mr="5" color="brand.gray.300" />
        <Text mx={4} fontSize="lg">
          Learning <strong>to speak</strong> a new language requires regular practice and
          repetition. <br /> The most effective practice comes from conversations with a native
          speaker.
        </Text>
        <Icon
          alignSelf={"flex-end"}
          as={ImQuotesRight}
          fontSize="2xl"
          mr="5"
          color="brand.gray.300"
        />
      </Flex>

      {/* <Box backgroundColor="gray.100" py={12}>
        <Section>
          <Flex flex="1" justifyContent={"center"} mr={{ md: "12" }}>
            <Image
              src="/images/talkio-business-3.png"
              alt="Woman speaking with AI"
              height={"auto"}
              alignSelf={"center"}
              width={{ base: "250px", sm: "350px", md: "450px", lg: "100%" }}
              boxShadow="lg"
              borderRadius={"lg"}
            />
          </Flex>
          <Flex flexDir={"column"} flex="1" justifyContent={"center"}>
            <Heading
              color={"brand.gray"}
              fontSize={{ base: "3xl", md: "4xl" }}
              fontWeight="bold"
              lineHeight={1}
              my={5}
            >
              Multi-User Management
            </Heading>
            <Text mt="4">
              Talkio for Teams is tailored for organizations of all sizes, from
              small startups to large corporations. It facilitates the
              management of multiple user profiles under a single administrative
              account, granting employees full access to {`Talkio's`} language
              learning features.
            </Text>
          </Flex>
        </Section>
      </Box> */}

      <Box id="pricing" backgroundColor="white">
        <Box as="section">
          <Container pt={{ base: "16", md: "24" }} mb="12">
            <Stack spacing={{ base: "16", md: "14" }} alignItems="center" textAlign={"center"}>
              <Stack spacing={{ base: "4", md: "6" }}>
                <Stack spacing="3">
                  <Heading size={{ base: "md", md: "lg" }}>{`Let's Talk`}</Heading>
                </Stack>
                <Text fontSize={{ base: "lg", md: "xl" }} color="fg.muted" maxW="3xl">
                  {`Ready to take your company's language learning to the next level? 
                    Contact us to book a demonstration and have a talk about how we can help your workplace.`}
                </Text>
                {/* <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="bold">
                  Starting at $10 per user
                </Text>

                <Button
                  mt={7}
                  size="xl"
                  colorScheme="brand.primary"
                  color="brand.gray.700"
                  maxW="400px"
                  mx="auto"
                  as="a"
                  href="/authentication/signup-organization"
                >
                  Sign Up & Customize Your Plan
                </Button> */}
                <Box w="100%" maxW="500px" mx="auto">
                  <TallyForm id="w4jazA" />
                </Box>

                {/* <Button
                  mt={7}
                  size="xl"
                  colorScheme="brand.primary"
                  color="brand.gray.700"
                  maxW="400px"
                  mx="auto"
                >
                  <BookBusinessDemo />
                </Button> */}
              </Stack>
            </Stack>
          </Container>
        </Box>
      </Box>

      <Box id="compliance-banner" bg="gray.100">
        <ComplianceBanner />
      </Box>
    </>
  );
}
